import {
  Bell, Check, CheckCheck, MessageSquare, Settings, Trash2, X, Headphones,
  Award, Medal, CalendarClock, AlarmClock, FileUp, ClipboardCheck, Gift, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useNotifications, Notification, NotificationCategory } from '@/contexts/NotificationContext';
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const CATEGORY_ICON: Record<NotificationCategory, typeof Bell> = {
  forum: MessageSquare,
  support: Headphones,
  deliverable: FileUp,
  evaluation: ClipboardCheck,
  certificate: Award,
  badge: Medal,
  session: CalendarClock,
  deadline: AlarmClock,
  referral: Gift,
  system: Info,
};

const CATEGORY_COLOR: Record<NotificationCategory, string> = {
  forum: 'text-blue-500',
  support: 'text-green-500',
  deliverable: 'text-orange-500',
  evaluation: 'text-primary',
  certificate: 'text-yellow-500',
  badge: 'text-yellow-500',
  session: 'text-primary',
  deadline: 'text-destructive',
  referral: 'text-purple-500',
  system: 'text-muted-foreground',
};

const NotificationItem = ({
  notification,
  onMarkAsRead,
  onDelete,
  onNavigate,
}: {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (path: string) => void;
}) => {
  const getTypeStyles = () => {
    switch (notification.type) {
      case 'warning':
        return 'border-l-4 border-l-orange-500';
      case 'error':
        return 'border-l-4 border-l-destructive';
      case 'success':
        return 'border-l-4 border-l-green-500';
      default:
        return 'border-l-4 border-l-primary/40';
    }
  };

  const Icon = CATEGORY_ICON[notification.category] ?? Info;

  const handleClick = () => {
    if (!notification.read) onMarkAsRead(notification.id);
    if (notification.link) onNavigate(notification.link);
  };

  return (
    <div
      className={cn(
        'p-3 transition-colors',
        getTypeStyles(),
        !notification.read && 'bg-accent/50',
        notification.link && 'cursor-pointer hover:bg-accent'
      )}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', CATEGORY_COLOR[notification.category])} />
            <p className="font-medium text-sm">{notification.title}</p>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {notification.message}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-2">
            {formatDistanceToNow(notification.createdAt, {
              addSuffix: true,
              locale: es,
            })}
          </p>
        </div>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {!notification.read && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onMarkAsRead(notification.id)}
            >
              <Check className="h-3 w-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={() => onDelete(notification.id)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    settings,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updateSettings,
  } = useNotifications();

  const [showSettings, setShowSettings] = useState(false);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">Notificaciones</h3>
          <div className="flex gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={markAllAsRead}
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Marcar todo
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowSettings(!showSettings)}
            >
              {showSettings ? (
                <X className="h-4 w-4" />
              ) : (
                <Settings className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {showSettings ? (
          <div className="p-4 space-y-4">
            <h4 className="font-medium text-sm">Preferencias</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="notif-in-app" className="text-sm">
                  Notificaciones en la plataforma
                </Label>
                <Switch
                  id="notif-in-app"
                  checked={settings.inApp}
                  onCheckedChange={(checked) =>
                    updateSettings({ ...settings, inApp: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="notif-reminders" className="text-sm">
                  Recordatorios de sesiones y entregas
                </Label>
                <Switch
                  id="notif-reminders"
                  checked={settings.reminders}
                  onCheckedChange={(checked) =>
                    updateSettings({ ...settings, reminders: checked })
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-[300px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {isLoading ? 'Cargando…' : 'No hay notificaciones'}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onDelete={deleteNotification}
                    onNavigate={navigate}
                  />
                ))}
                {hasMore && (
                  <div className="p-2 text-center">
                    <Button variant="ghost" size="sm" className="text-xs" onClick={loadMore} disabled={isLoading}>
                      Ver más
                    </Button>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
